// Shared Order Confirmation Components
// Used by both Customer and Affiliate confirmation pages

import React, { useState } from "react";
import Button from "../Button";
import "./OrderConfirmation.css";
import { t } from "i18next";

/**
 * Success Header Component
 * Displays the confirmation success message with icon
 */
export const SuccessHeader = ({ title, subtitle, orderNumber, orderDate }) => {
  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <section className="success-header">
      <div className="success-icon-wrapper">
        <div className="success-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5 13l4 4L19 7"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
      <h1 className="success-title">{title}</h1>
      <p className="success-subtitle">{subtitle}</p>
      {(orderNumber || orderDate) && (
        <p className="success-meta">
          {orderNumber && `Order #${orderNumber}`}
          {orderNumber && orderDate && " • "}
          {orderDate && `Placed on ${formatDate(orderDate)}`}
        </p>
      )}
    </section>
  );
};

/**
 * Order Summary Card Component
 * Displays order details in a styled card with optional CTA
 */
export const OrderSummaryCard = ({
  orderId,
  orderDate,
  userId,
  userIdPrefix = "C",
  packageName,
  imageUrl,
  price,
  discount = 0,
  tax,
  total,
  currencyCode = "USD",
  footerText = "A confirmation email has been sent to your inbox.",
  ctaButton = null,
  ctaHelperText = null,
  onCtaClick = null,
}) => {
  const formatCurrency = (amount) => {
    return (amount || 0).toLocaleString("en-US", {
      style: "currency",
      currency: currencyCode,
    });
  };

  return (
    <section className="order-summary-section">
      <div className="order-summary-card">
        {/* Title with Icon */}
        <div className="card-header">
          <svg
            className="card-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
          <h2 className="card-title">Order Summary</h2>
        </div>

        {/* Product/Package - simplified display */}
        <div className="order-product-simple">
          <div className="product-image-placeholder">
            {imageUrl ? (
              <img
                src={
                  imageUrl
                    ? imageUrl.includes("http")
                      ? `${imageUrl}`
                      : `https://sqlvaqdxcel5sg4jky.blob.core.windows.net/documentstorage/${imageUrl}`
                    : "product"
                }
              />
            ) : (
              <span>{t("Product")}</span>
            )}
          </div>
          <div className="product-info">
            <div className="product-name-qty">
              <span className="product-name">
                {packageName || "NOA Premium"}
              </span>
              <span className="product-qty">Qty: 1</span>
            </div>
          </div>
          <span className="product-price">{formatCurrency(price)}</span>
        </div>

        {/* Pricing breakdown */}
        <div className="order-pricing">
          <div className="pricing-row">
            <span>Subtotal</span>
            <span>{formatCurrency(price)}</span>
          </div>
          {discount > 0 && (
            <div className="pricing-row discount">
              <span>Discount</span>
              <span className="discount-value">
                -{formatCurrency(discount)}
              </span>
            </div>
          )}
          <div className="pricing-row">
            <span>Tax</span>
            <span>{formatCurrency(tax)}</span>
          </div>
          <div className="pricing-row total">
            <span>Total</span>
            <span>{formatCurrency(total)}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * Primary CTA Button Component
 * Large action button using Button component with outline variant (no stars)
 */
export const PrimaryCTA = ({ onClick, buttonText, helperText, variant }) => {
  return (
    <section className="access-product-section">
      <Button text={buttonText} onClick={onClick} variant={variant} />
      {helperText && <p className="helper-text">{helperText}</p>}
    </section>
  );
};

/**
 * Referral Section Component
 * Displays referral link with copy functionality and optional inline social share
 */
export const ReferralSection = ({
  title,
  description,
  referralLink,
  badge,
  variant = "customer", // "customer" or "affiliate"
  showInlineSocial = false,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOnFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        referralLink
      )}`,
      "_blank"
    );
  };

  const shareOnTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        referralLink
      )}&text=${encodeURIComponent("Join me on NOA!")}`,
      "_blank"
    );
  };

  const shareViaEmail = () => {
    window.open(
      `mailto:?subject=${encodeURIComponent(
        "Join NOA!"
      )}&body=${encodeURIComponent(
        `I'd like to invite you to join NOA! Use my referral link: ${referralLink}`
      )}`,
      "_blank"
    );
  };

  return (
    <section className="refer-free-section">
      <div className={`refer-free-card ${variant}`}>
        {/* Title with Share Icon */}
        <div className="refer-header">
          <svg
            className="refer-header-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
          <h2 className="refer-title">{title}</h2>
        </div>

        {/* Your Referral Link Label */}
        <label className="referral-link-label">Your Referral Link</label>

        {/* Referral Link Box */}
        <div className="referral-link-box">
          <div className="link-display">
            <span className="link-text">{referralLink}</span>
          </div>
          <button
            className="copy-link-btn"
            onClick={handleCopyLink}
            title="Copy link"
          >
            {copied ? (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            )}
          </button>
        </div>

        {/* Inline Social Share Buttons */}
        {showInlineSocial && (
          <div className="inline-social-buttons">
            <button
              onClick={shareOnFacebook}
              className="social-share-btn"
              title="Facebook"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              <span>Facebook</span>
            </button>
            <button
              onClick={shareOnTwitter}
              className="social-share-btn"
              title="Twitter"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
              </svg>
              <span>Twitter</span>
            </button>
            <button
              onClick={shareViaEmail}
              className="social-share-btn"
              title="Email"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              <span>Email</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

/**
 * Social Share Buttons Component
 * Buttons for sharing referral link on social platforms
 */
export const SocialShareButtons = ({ referralLink, description }) => {
  const shareOnFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        referralLink
      )}`,
      "_blank"
    );
  };

  const shareOnWhatsApp = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(
        `Join me on NOA! ${referralLink}`
      )}`,
      "_blank"
    );
  };

  const shareOnMessenger = () => {
    window.open(
      `https://www.facebook.com/dialog/send?link=${encodeURIComponent(
        referralLink
      )}&app_id=YOUR_APP_ID`,
      "_blank"
    );
  };

  const shareViaSMS = () => {
    window.open(
      `sms:?body=${encodeURIComponent(`Join me on NOA! ${referralLink}`)}`,
      "_blank"
    );
  };

  const shareViaEmail = () => {
    window.open(
      `mailto:?subject=${encodeURIComponent(
        "Join NOA!"
      )}&body=${encodeURIComponent(
        `I'd like to invite you to join NOA! Use my referral link: ${referralLink}`
      )}`,
      "_blank"
    );
  };

  return (
    <section className="social-share-section">
      <h2 className="section-title">Share Your Referral Link</h2>
      <p className="section-description">
        {description || "Invite friends through your favorite platforms."}
      </p>

      <div className="social-buttons-grid">
        <button onClick={shareOnFacebook} className="social-btn facebook">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
          <span>Share on Facebook</span>
        </button>

        <button onClick={shareOnWhatsApp} className="social-btn whatsapp">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          <span>Share via WhatsApp</span>
        </button>

        <button onClick={shareOnMessenger} className="social-btn messenger">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.498 1.744 6.614 4.469 8.654V24l4.088-2.242c1.092.301 2.246.464 3.443.464 6.627 0 12-4.975 12-11.111S18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8l3.131 3.259L19.752 8l-6.561 6.963z" />
          </svg>
          <span>Send in Messenger</span>
        </button>

        <button onClick={shareViaSMS} className="social-btn sms">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>
          <span>Send via Text Message</span>
        </button>

        <button onClick={shareViaEmail} className="social-btn email">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
          <span>Send via Email</span>
        </button>
      </div>
    </section>
  );
};

/**
 * Step Card Component
 * Individual step in the how-it-works section
 */
export const StepCard = ({ number, title, description, icon, highlight }) => {
  return (
    <div className={`step-card ${highlight ? "highlight" : ""}`}>
      <div className="step-number">{number}</div>
      <div className="step-content">
        <h3 className="step-title">{title}</h3>
        <p className="step-description">{description}</p>
      </div>
      <div className="step-icon">{icon}</div>
    </div>
  );
};

/**
 * Step Connector Component
 * Arrow between steps
 */
export const StepConnector = () => {
  return (
    <div className="step-connector">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M5 12h14M12 5l7 7-7 7" />
      </svg>
    </div>
  );
};

/**
 * How It Works Section Component
 * Configurable steps explainer
 */
export const HowItWorksSection = ({ title, steps }) => {
  const stepIcons = {
    share: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
    users: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    star: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ),
    cart: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="9" cy="21" r="1" />
        <circle cx="20" cy="21" r="1" />
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
      </svg>
    ),
    link: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    ),
    dollar: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
  };

  return (
    <section className="how-it-works-section">
      <h2 className="section-title">{title}</h2>

      <div className="steps-container">
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            <StepCard
              number={index + 1}
              title={step.title}
              description={step.description}
              icon={stepIcons[step.icon] || stepIcons.star}
              highlight={step.highlight}
            />
            {index < steps.length - 1 && <StepConnector />}
          </React.Fragment>
        ))}
      </div>
    </section>
  );
};

/**
 * FAQ Section Component
 * Expandable FAQ items
 */
export const FAQSection = ({ title = "Frequently Asked Questions", items }) => {
  const [activeFaq, setActiveFaq] = useState(null);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  return (
    <section className="faq-section">
      <h2 className="faq-title">{title}</h2>
      <div className="faq-container">
        {items.map((item, index) => (
          <div
            key={index}
            className={`faq-item ${activeFaq === index ? "active" : ""}`}
            onClick={() => toggleFaq(index)}
          >
            <div className="faq-question">
              <span>{item.question}</span>
              <div className="faq-toggle">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline
                    points={
                      activeFaq === index ? "18 15 12 9 6 15" : "6 9 12 15 18 9"
                    }
                  />
                </svg>
              </div>
            </div>
            <div className="faq-answer">
              <p>{item.answer}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/**
 * Social Proof Section Component
 * Testimonials and success metrics
 */
export const SocialProofSection = ({ title, testimonials }) => {
  return (
    <section className="social-proof-section">
      <div className="social-proof-header">
        <span className="proof-badge">COMMUNITY SUCCESS</span>
        <h2 className="proof-title">{title}</h2>
      </div>

      <div className="testimonials-container">
        {testimonials.map((testimonial, index) => (
          <div key={index} className="testimonial-card">
            <div className="quote-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>
            </div>
            <p className="testimonial-text">{testimonial.text}</p>
            <p className="testimonial-author">— {testimonial.author}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

/**
 * Compliance Footer Component
 * Small print compliance reminder
 */
export const ComplianceFooter = ({ text }) => {
  return (
    <footer className="compliance-footer">
      <p className="compliance-text">{text}</p>
    </footer>
  );
};

/**
 * Confirmation Row Component
 * Side-by-side layout wrapper for two equal sections
 */
export const ConfirmationRow = ({ children }) => {
  return <div className="confirmation-row">{children}</div>;
};

/**
 * Order Confirmation Page Wrapper
 * Main container for confirmation pages
 */
export const OrderConfirmationWrapper = ({ children }) => {
  return (
    <div className="order-confirmation-page">
      <div className="confirmation-container">{children}</div>
    </div>
  );
};
